import React, { useState } from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { utcToZonedTime, toDate } from "date-fns-tz";
import { StyledRow, Title, ShowMoreText } from "./TemplateOffersListHelpers";
import { useStaticQuery, graphql, Link } from "gatsby";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Card from "react-bootstrap/Card";
import OfferSearchModal from "../OfferSearchModal/OfferSearchModal";
import { setSearch } from "../../../../redux/slices/Search/search";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { ITemplateOffersListProps } from "./TemplateOffersListProps";

const TemplateOffersList: React.FC<ITemplateOffersListProps> = (props) => {
  const dispatch = useAppDispatch();
  const [showMore, setShowMore] = useState(false);
  const [showOffersCount, setShowOffersCount] = useState(3);
  const [minLOS, setMinLOS] = useState<string | null>(null);
  const [minLeadDays, setMinLeadDays] = useState<string | null>(null);

  const excludeOfferId = props.excludeOfferId || null;
  const crsCode = props.crsCode || null;
  const data = useStaticQuery(graphql`
    {
      page: nodePage(path: { alias: { eq: "/v1/offers" } }) {
        id
        relationships {
          paragraphs: field_sections {
            type: __typename
            ...ParagraphSiteOffers
          }
        }
      }
    }
  `);

  let allOffers = [];

  const timeZone = "America/New_York";
  const currentTime = utcToZonedTime(new Date(), timeZone);

  props.offers.forEach((offer) => {
    if (
      offer.field_publish_start_date_time &&
      offer.field_unpublish_start_date_time
    ) {
      const ShowDate = toDate(offer.field_publish_start_date_time);
      const ShowUSTime = utcToZonedTime(ShowDate, timeZone);

      const HideDate = toDate(offer.field_unpublish_start_date_time);
      const HideUSTime = utcToZonedTime(HideDate, timeZone);

      if (
        currentTime.getTime() >= ShowUSTime.getTime() &&
        currentTime.getTime() <= HideUSTime.getTime()
      ) {
        allOffers.push(offer);
      }
    } else {
      allOffers.push(offer);
    }
  });
  if (props.offers.length < 3) {
    const itemsToFill = 3 - props.offers.length;
    const siteOffersParagraph = data.page.relationships.paragraphs.filter(
      (paragraph: any) => paragraph.type === "paragraph__site_offers"
    );
    if (siteOffersParagraph && siteOffersParagraph.length > 0) {
      const siteOffers = siteOffersParagraph[0].relationships.offers;
      if (siteOffers) {
        for (let i = 0; i < itemsToFill; i++) {
          // Only add offers that don't already exist in allOffers array.
          const exists = allOffers.find(
            (offer) => offer.id === siteOffers[i].id
          );
          if (exists === undefined && siteOffers[i].id !== excludeOfferId) {
            allOffers.push({ ...siteOffers[i], siteOffer: true });
          }
        }
      }
    }
  }
  allOffers =
    allOffers &&
    allOffers.filter(
      (offer: {
        relationships: {
          field_site_list?: { machine_name: string | undefined };
        };
      }) =>
        offer.relationships.field_site_list?.machine_name ==
        process.env.GATSBY_SITE_MACHINE_NAME
    );

  if (allOffers.length > 3) {
    allOffers = allOffers.splice(0, showOffersCount);
  }

  const [showOfferModal, setShowOfferModal] = React.useState(false);
  const [rate, setRate] = React.useState({});
  const search = useAppSelector((state) => state.search);

  const handleBookNow = (event: any) => {
    const rateCode = event.target.dataset.rate;
    if (crsCode) {
      const discountCodes = ["AAA", "AARP", "GOV"];
      const updatedSearch = { ...search };
      if (discountCodes.includes(rateCode)) {
        updatedSearch.discount = rateCode;
        updatedSearch.groupCode = null;
        updatedSearch.promotionCode = null;
      } else {
        updatedSearch.discount = null;
        updatedSearch.groupCode = null;
        updatedSearch.promotionCode = rateCode;
      }
      dispatch(setSearch(updatedSearch));
      const element = document.getElementById("rooms");
      if (element) {
        element.scrollIntoView(true);
      }
      props.onBookNow && props.onBookNow();
    } else {
      const offerName = event.target.dataset.offername;
      const selectedOffer = props.offers?.find(
        (offer) => offer?.field_promo_code === rateCode
      );
      if (selectedOffer) {
        setMinLOS(selectedOffer.field_minimum_length_stay || null);
        setMinLeadDays(selectedOffer.field_lead_days || null);
      }
      setRate({
        code: rateCode,
        name: offerName,
      });
      setShowOfferModal(true);
    }
  };

  const handleShowMore = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowOffersCount(!showMore ? props.offers.length : 3);
    setShowMore(!showMore);

    if (showMore) {
      setTimeout(() => {
        const showMoreButton = document.getElementById(
          "show-more-offer-button"
        );
        if (showMoreButton) {
          showMoreButton.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 30);
    }
  };

  return (
    <>
      <StyledRow crscode={crsCode} id={"template-offers"}>
        {allOffers.map((offer, idx) => {
          const rateCode = offer.field_promo_code;
          const image = offer.relationships.field_teaser_image
            ? getImage(
                offer.relationships.field_teaser_image.relationships
                  .field_media_image.gatsbyImage as any
              )
            : null;
          const offerPath =
            !offer.siteOffer && props.hotelPath
              ? `${props.hotelPath}${offer.path.alias}`
              : offer.path.alias;
          if (image) {
            return (
              <Col
                key={`${offer.id}-${idx}`}
                lg={4}
                className="mb-4 mt-4 mb-md-0 offer"
              >
                <Card
                  className="border-0 h-100"
                  id={`template-offer-${idx + 1}`}
                >
                  <Card.Img
                    as={GatsbyImage}
                    image={image}
                    alt={offer.title}
                    variant="top"
                    id={`offer-image-${idx + 1}`}
                  />
                  <Card.Body className="templateOfferCard text-center d-flex flex-column">
                    {offer.field_subtitle && (
                      <Badge
                        className="border mb-2 align-self-center darkText"
                        id={`template-offer-subtitle`}
                      >
                        {offer.field_subtitle}
                      </Badge>
                    )}
                    <Title id={`offer-title`}>{offer.title}</Title>
                    <Card.Text
                      id={`template-offer-desc`}
                      className="description text-center"
                      dangerouslySetInnerHTML={{
                        __html: offer.description?.summary,
                      }}
                    />
                  </Card.Body>
                  <Card.Footer className="border-0 bg-white d-flex flex-column ps-3 pe-3 pb-3 ps-md-4 pe-md-4 pb-md-4">
                    {rateCode && (
                      <Button
                        data-rate={rateCode}
                        id={"book-now-button-" + rateCode.toLowerCase()}
                        data-offername={offer.title}
                        onClick={handleBookNow}
                        className="mb-3 bookNow"
                      >
                        Book Now
                      </Button>
                    )}
                    {!offer.field_external_link &&
                      offer.description?.processed && (
                        <Link
                          to={offerPath}
                          aria-label={`Learn More for ${offer.title}`}
                          className="teaser-link align-self-center"
                          id={`offer-teaser-internal-link-${rateCode}`}
                        >
                          Learn More
                        </Link>
                      )}
                    {offer.field_external_link && (
                      <a
                        href={offer.field_external_link.url}
                        className="teaser-link align-self-center"
                        target="_blank"
                        id={`offer-teaser-external-link-${rateCode}`}
                        rel="noreferrer"
                      >
                        {offer.field_external_link.title}
                      </a>
                    )}
                  </Card.Footer>
                </Card>
              </Col>
            );
          }
          return null;
        })}
        {props.offers?.length > 3 ? (
          <ShowMoreText
            onClick={handleShowMore}
            id={`show-more-offer-button`}
            className="mt-2 showMoreOffers"
            aria-expanded={showMore}
          >
            {showMore ? "Show Fewer Offers -" : "Show More Offers +"}
          </ShowMoreText>
        ) : (
          ""
        )}
      </StyledRow>
      <OfferSearchModal
        show={showOfferModal}
        onHide={() => {
          setShowOfferModal(false);
        }}
        location={props.location}
        rate={rate}
        minLOS={minLOS}
        minLeadDays={minLeadDays}
      />
    </>
  );
};

export default TemplateOffersList;
